import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import useBonus from 'hooks/useBonus';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import SkeletonBonuses from './skeletonBonuses';
import Skeleton from 'components/skeleton/Skeleton';
import { ActionButton } from 'components/actionButton';
import { LockOutlineIcon, ChevronDownIcon } from 'components/icons';
import FreeSpinDetails from './freeSpinDetails';
import { BonusCountdown } from './bonusCountdown';
import { generateIdempotencyKey } from 'utils/idempotency';
import styles from './bonuses.module.scss';

const Bonuses: React.FC = () => {
  const { t } = useTranslation();
  const {
    bonuses,
    loading,
    isFetching,
    error,
    bonusesError,
    redeemPromoCode,
    claimBonus,
    refetch,
  } = useBonus();
  const dispatch = useAppDispatch();
  const [promoCode, setPromoCode] = useState('');
  const [isPromoOpen, setIsPromoOpen] = useState(true);
  const [redeemFeedback, setRedeemFeedback] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [pendingBonuses, setPendingBonuses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const pendingIds = Object.keys(pendingBonuses).filter((id) => pendingBonuses[id]);
    if (pendingIds.length > 0) {
      const interval = setInterval(() => {
        refetch();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [pendingBonuses, refetch]);

  useEffect(() => {
    bonuses.forEach((b) => {
      if (pendingBonuses[b.id]) {
        const hasClaim = Array.isArray(b.claimable) && b.claimable.length > 0;
        const isTimerReset = b.unlocksAt ? new Date(b.unlocksAt).getTime() > Date.now() : false;

        if (hasClaim || isTimerReset) {
          setPendingBonuses((prev) => ({ ...prev, [b.id]: false }));
        }
      }
    });
  }, [bonuses, pendingBonuses]);
  const [isFreeSpinModalOpen, setIsFreeSpinModalOpen] = useState(false);
  const [freeSpinAmount, setFreeSpinAmount] = useState<number>(0);
  const { user, isAuthenticated: rawIsAuth } = useAppSelector((state) => state.auth);
  const { subLevels } = useAppSelector((state) => state.level);
  const config = useAppSelector((state) => state.site.config);
  const isAuthenticated = rawIsAuth && !config?.geo?.isBlocked;

  const TIER_ORDER = useMemo(() => {
    const allLevels = Object.values(subLevels).flat() as any[];
    allLevels.sort((a, b) => a.index - b.index);
    return Array.from(new Set(allLevels.map((l) => l.tierName.toLowerCase())));
  }, [subLevels]);

  const sortedBonuses = useMemo(() => {
    if (!bonuses) return [];

    // Find special items to place at the start and end
    const rakeback = bonuses.find((b) => b.id === 'rakeback');
    const vipBonus = bonuses.find((b) => b.id === 'vip_bonus');

    // Filter out standard bonuses
    const standardBonuses = bonuses.filter((b) => {
      return b.id !== 'rakeback' && b.id !== 'vip_bonus';
    });

    // Reconstruct array: Rakeback first, standard bonuses in the middle, VIP Bonus last
    return [...(rakeback ? [rakeback] : []), ...standardBonuses, ...(vipBonus ? [vipBonus] : [])];
  }, [bonuses]);

  const visibleBonuses = useMemo(() => {
    return sortedBonuses.filter((card) => !card.locked);
  }, [sortedBonuses]);

  const handleClaim = async (bonusType: string) => {
    if (bonusType === 'free_spin') {
      const card = visibleBonuses.find((b) => b.id === 'free_spin');
      const amount = card?.claimable?.[0] ? parseFloat(card.claimable[0].amount) : 0;
      setFreeSpinAmount(amount);
      setIsFreeSpinModalOpen(true);
      return;
    }
    setActivatingId(bonusType);
    await claimBonus(bonusType, undefined, generateIdempotencyKey());
    setActivatingId(null);
  };

  const handleFreeSpinClaim = async (gameId: number, betLevel: number) => {
    setActivatingId('free_spin');
    const res = await claimBonus('free_spin', { gameId, betLevel }, generateIdempotencyKey());
    setActivatingId(null);
    if (res.success) {
      setIsFreeSpinModalOpen(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || isRedeeming) return;

    setIsRedeeming(true);
    setRedeemFeedback(null);

    const res = await redeemPromoCode(promoCode.trim());

    if (res.success) {
      setRedeemFeedback({ message: t('rewards.bonuses.promoSuccess'), type: 'success' });
      dispatch(showToast({ text: t('rewards.bonuses.promoSuccess') }));
      setPromoCode('');
    } else {
      setRedeemFeedback({
        message: res.error || t('rewards.bonuses.promoErrorFallback'),
        type: 'error',
      });
    }
    setIsRedeeming(false);
  };

  return (
    <div className={styles.scrollableContent} style={{ position: 'relative' }}>
      <div className={styles.bonusGrid}>
        {loading ? (
          <SkeletonBonuses length={8} />
        ) : bonusesError ? (
          <div className={styles.errorState}>{t('rewards.bonuses.errorLoading')}</div>
        ) : visibleBonuses.length === 0 ? (
          <div className={styles.emptyState}>{t('rewards.bonuses.emptyState')}</div>
        ) : (
          visibleBonuses.map((card) => {
            const userTier = user?.level?.current?.tierName?.toLowerCase() || '';
            const requiredTier = card.startTier?.toLowerCase() || '';
            const userTierIndex = TIER_ORDER.indexOf(userTier);
            const requiredTierIndex = TIER_ORDER.indexOf(requiredTier);
            const isUnlocked =
              isAuthenticated &&
              (card.id === 'lossback' ||
                card.id === 'vip_bonus' ||
                (userTierIndex >= requiredTierIndex && requiredTierIndex !== -1));

            const claimableData = card.claimable?.[0];
            const hasClaimable =
              isAuthenticated && claimableData && parseFloat(claimableData.amount) > 0;
            const claimAmount = claimableData
              ? parseFloat(claimableData.amount).toFixed(2)
              : '0.00';

            return (
              <div key={card.id} className={styles.bonusCard}>
                <div className={styles.iconWrapper}>
                  <img src={card?.info?.imageUrl ?? ''} alt={card.title} width={96} height={96} />
                </div>
                <h4>{card?.info?.title}</h4>
                <p className={styles.description}>{card?.info?.bonusIncludes}</p>
                {isAuthenticated &&
                card.unlocksAt != null &&
                !hasClaimable &&
                !pendingBonuses[card.id] ? (
                  <BonusCountdown
                    unlocksAt={card.unlocksAt}
                    onComplete={() => setPendingBonuses((prev) => ({ ...prev, [card.id]: true }))}
                  />
                ) : (
                  <div className={styles.statCard}>
                    <ActionButton
                      className={cx(styles.claimBtn, {
                        [styles.locked]:
                          (!isUnlocked && !hasClaimable && !pendingBonuses[card.id]) ||
                          (isUnlocked &&
                            !hasClaimable &&
                            (card.id === 'lossback' || card.id === 'vip_bonus')),
                        [styles.preClaim]:
                          isUnlocked &&
                          !hasClaimable &&
                          card.id !== 'lossback' &&
                          card.id !== 'vip_bonus',
                      })}
                      disabled={
                        (!isUnlocked && !hasClaimable && !pendingBonuses[card.id]) ||
                        (isUnlocked && !hasClaimable && !pendingBonuses[card.id]) ||
                        isFetching
                      }
                      onClick={hasClaimable && !isFetching ? () => handleClaim(card.id) : undefined}
                      isLoading={activatingId === card.id || pendingBonuses[card.id]}
                    >
                      {pendingBonuses[card.id] ? (
                        t('rewards.bonuses.preparing')
                      ) : isFetching ? (
                        <Skeleton
                          style={{
                            width: '70%',
                            height: '18px',
                            borderRadius: '4px',
                            margin: '0 auto',
                            opacity: 0.7,
                          }}
                        />
                      ) : (
                        <>
                          {!isUnlocked && !hasClaimable && (
                            <LockOutlineIcon className={styles.lockIcon} />
                          )}
                          {isUnlocked ? (
                            t('rewards.bonuses.claim', { amount: claimAmount })
                          ) : (
                            <>
                              {t('rewards.bonuses.reachTier', {
                                tier: (() => {
                                  if (!card.startTier) return '';
                                  const tName =
                                    subLevels?.[card.startTier.toLowerCase()]?.[0]?.tierName ||
                                    card.startTier;
                                  return tName.charAt(0).toUpperCase() + tName.slice(1);
                                })(),
                              })}
                            </>
                          )}
                        </>
                      )}
                    </ActionButton>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {isAuthenticated && (
        <div className={styles.promoSection}>
          <button className={styles.promoHeader} onClick={() => setIsPromoOpen(!isPromoOpen)}>
            <h3>{t('rewards.bonuses.redeemPromo')}</h3>
            <ChevronDownIcon className={`${styles.chevron} ${isPromoOpen ? styles.open : ''}`} />
          </button>

          {isPromoOpen && (
            <div className={styles.promoContent}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder={t('rewards.bonuses.promoPlaceholder')}
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    if (redeemFeedback) setRedeemFeedback(null);
                  }}
                  disabled={isRedeeming}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                />
                {redeemFeedback && (
                  <div className={cx(styles.promoMessage, styles[redeemFeedback.type])}>
                    {redeemFeedback.message}
                  </div>
                )}
              </div>
              <ActionButton
                className={styles.applyBtn}
                onClick={handleApplyPromo}
                disabled={!promoCode.trim()}
                isLoading={isRedeeming}
              >
                {t('rewards.bonuses.apply')}
              </ActionButton>
            </div>
          )}
        </div>
      )}

      {isFreeSpinModalOpen && (
        <FreeSpinDetails
          onBack={() => setIsFreeSpinModalOpen(false)}
          onClaim={handleFreeSpinClaim}
          isLoadingClaim={activatingId === 'free_spin'}
          claimAmount={freeSpinAmount}
        />
      )}
    </div>
  );
};

export default Bonuses;
